import React from "react";
import DOMPurify from "dompurify";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const normalizeText = (value = "") => String(value ?? "").trim();

export const EMAIL_PREVIEW_BRAND = {
  logoUrl: "https://elitepromomarketing.com/wp-content/uploads/2024/08/logo-1-600x183.png",
  company: "Elite Promotional Marketing",
  darkGray: "#2E2E2E",
  lightGray: "#D1D5DB",
  text: "#111111",
  background: "#F7F7F7",
  font: "'Inter', Arial, Helvetica, sans-serif",
};

export const EMAIL_TEMPLATE_PREVIEW_RENDERERS = {
  elite: "elite",
  elite_template: "elite",
  plain: "plain",
  no_template: "plain",
};

export const normalizeEmailViewerLayout = (layout) =>
  EMAIL_TEMPLATE_PREVIEW_RENDERERS[String(layout || "").toLowerCase()] || "plain";

export const getEmailLayoutLabel = (layout) =>
  normalizeEmailViewerLayout(layout) === "elite"
    ? "Elite template"
    : "Basic email (no template)";

export const sanitizeEmailViewerHtml = (html) =>
  DOMPurify.sanitize(String(html || ""), {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
    ALLOW_DATA_ATTR: false,
  });

const toDisplayString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return normalizeText(value);
  if (typeof value === "object") {
    const name = normalizeText(
      value.name ||
      value.label ||
      [value.firstName, value.lastName].filter(Boolean).join(" ")
    );
    const email = normalizeText(value.email || value.address || value.id);
    if (name && email && name.toLowerCase() !== email.toLowerCase()) {
      return `${name} <${email}>`;
    }
    return name || email;
  }
  return normalizeText(value);
};

const recipientSummary = (recipients = []) => {
  const list = Array.isArray(recipients) ? recipients : [recipients];
  return list.map(toDisplayString).filter(Boolean).join(", ");
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const resolveAttachmentName = (attachment, index) =>
  normalizeText(
    attachment?.fileName ||
    attachment?.originalFileName ||
    attachment?.filename ||
    attachment?.name ||
    attachment?.title
  ) || `Attachment ${index + 1}`;

const isSafeAttachmentUrl = (value) => /^(https?:\/\/|\/)/i.test(normalizeText(value));

const renderBody = ({ htmlBody, textBody, fontFamily, textColor }) => {
  const safeHtml = sanitizeEmailViewerHtml(htmlBody);
  if (normalizeText(safeHtml)) {
    return (
      <Box
        sx={{
          color: textColor,
          fontFamily,
          fontSize: 15,
          lineHeight: 1.7,
          "& p": { mt: 0, mb: 1.5 },
          "& ul, & ol": { mt: 0, mb: 1.5, pl: 3 },
          "& li": { mb: 0.5 },
          "& a": { color: "#1A73E8" },
          "& h1, & h2, & h3, & h4": { mt: 0, mb: 1.5, lineHeight: 1.25 },
          "& img": { maxWidth: "100%", height: "auto" },
          "& table": { maxWidth: "100%", borderCollapse: "collapse" },
          "& blockquote": {
            borderLeft: "3px solid #d0d7de",
            pl: 1.5,
            ml: 0,
            color: "text.secondary",
          },
          "& pre": {
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "'IBM Plex Mono', 'Consolas', monospace",
            bgcolor: "#f8fafc",
            borderRadius: 1.5,
            p: 1.5,
          },
        }}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  return (
    <Typography
      variant="body1"
      sx={{
        color: textColor,
        fontFamily,
        fontSize: 15,
        lineHeight: 1.75,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {normalizeText(textBody) || "(No email body provided)"}
    </Typography>
  );
};

const AttachmentSection = ({ attachments = [] }) => {
  if (!attachments.length) return null;
  return (
    <Box>
      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary", fontWeight: 700 }}>
        Attachments
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {attachments.map((attachment, index) => {
          const label = resolveAttachmentName(attachment, index);
          const url = normalizeText(attachment?.url || attachment?.href || attachment?.downloadUrl);
          if (isSafeAttachmentUrl(url)) {
            return (
              <Chip
                key={`${label}-${index}`}
                component="a"
                clickable
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                label={label}
                size="small"
                variant="outlined"
              />
            );
          }
          return <Chip key={`${label}-${index}`} label={label} size="small" variant="outlined" />;
        })}
      </Stack>
    </Box>
  );
};

const ForwardedMessageSection = ({ forwardedMessage, fontFamily, textColor }) => {
  if (!forwardedMessage || typeof forwardedMessage !== "object") return null;
  const rows = [
    { label: "From", value: forwardedMessage.from || toDisplayString({ name: forwardedMessage.fromName, email: forwardedMessage.fromEmail }) },
    { label: "To", value: recipientSummary(forwardedMessage.to) },
    { label: "CC", value: recipientSummary(forwardedMessage.cc) },
    { label: "Sent", value: forwardedMessage.sentAt || forwardedMessage.date },
    { label: "Subject", value: forwardedMessage.subject },
  ];
  const htmlBody = forwardedMessage.htmlBody || forwardedMessage.fullHtmlBody || "";
  const textBody = forwardedMessage.textBody || "";
  const hasBody = normalizeText(htmlBody) || normalizeText(textBody);
  const hasRows = rows.some((row) => normalizeText(row.value));
  if (!hasBody && !hasRows) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: textColor, fontFamily }}>
        Forwarded message
      </Typography>
      <EmailHeaderRows rows={rows} fontFamily={fontFamily} />
      {hasBody ? (
        <Box sx={{ mt: hasRows ? 2 : 0 }}>
          {renderBody({ htmlBody, textBody, fontFamily, textColor })}
        </Box>
      ) : null}
    </Box>
  );
};

const EmailHeaderRows = ({ rows = [], fontFamily }) => {
  const validRows = rows.filter((row) => normalizeText(row?.value));
  if (!validRows.length) return null;
  return (
    <Stack spacing={0.5}>
      {validRows.map((row) => (
        <Typography
          key={`${row.label}-${row.value}`}
          variant="body2"
          sx={{
            color: "text.secondary",
            fontFamily,
            lineHeight: 1.45,
            wordBreak: "break-word",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
            {row.label}:
          </Box>{" "}
          {row.value}
        </Typography>
      ))}
    </Stack>
  );
};

const PlainEmailViewer = ({
  subject,
  fromName,
  fromEmail,
  to,
  cc,
  bcc,
  sentAt,
  receivedAt,
  htmlBody,
  textBody,
  attachments,
  forwardedMessage,
  extraHeaderRows = [],
}) => {
  const senderLabel = normalizeText(fromName || fromEmail || "Unknown sender");
  const senderSubLabel = normalizeText(fromEmail && fromName && fromName !== fromEmail ? fromEmail : "");
  const avatarText = (senderLabel || "?").slice(0, 2).toUpperCase();
  const headerRows = [
    { label: "To", value: recipientSummary(to) },
    { label: "CC", value: recipientSummary(cc) },
    { label: "BCC", value: recipientSummary(bcc) },
    { label: sentAt ? "Sent" : "Received", value: formatTimestamp(sentAt || receivedAt) },
    ...extraHeaderRows,
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: "#fff",
        borderColor: "#dbe2ea",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          borderBottom: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%)",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 2 }}>
          {normalizeText(subject) || "Untitled email"}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ width: 44, height: 44, bgcolor: "#1f4b99", fontWeight: 700 }}>
            {avatarText}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {senderLabel}
            </Typography>
            {senderSubLabel ? (
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.25 }}>
                {senderSubLabel}
              </Typography>
            ) : (
              <Box sx={{ mb: 1.25 }} />
            )}
            <EmailHeaderRows rows={headerRows} />
          </Box>
        </Stack>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2.25, sm: 3 } }}>
        {forwardedMessage ? (
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: "#111827" }}>
            Forwarder's message
          </Typography>
        ) : null}
        {renderBody({ htmlBody, textBody, fontFamily: "'Segoe UI', Arial, sans-serif", textColor: "#111827" })}
        <ForwardedMessageSection
          forwardedMessage={forwardedMessage}
          fontFamily="'Segoe UI', Arial, sans-serif"
          textColor="#111827"
        />
      </Box>
      {attachments.length ? (
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2.25, sm: 3 } }}>
          <Divider sx={{ mb: 2 }} />
          <AttachmentSection attachments={attachments} />
        </Box>
      ) : null}
    </Paper>
  );
};

const EliteTemplateEmailViewer = ({
  subject,
  fromName,
  fromEmail,
  to,
  cc,
  bcc,
  sentAt,
  receivedAt,
  htmlBody,
  textBody,
  attachments,
  forwardedMessage,
  extraHeaderRows = [],
}) => {
  const headerRows = [
    { label: "From", value: toDisplayString({ name: fromName, email: fromEmail }) },
    { label: "To", value: recipientSummary(to) },
    { label: "CC", value: recipientSummary(cc) },
    { label: "BCC", value: recipientSummary(bcc) },
    { label: sentAt ? "Sent" : "Received", value: formatTimestamp(sentAt || receivedAt) },
    ...extraHeaderRows,
  ];

  return (
    <Box
      sx={{
        bgcolor: EMAIL_PREVIEW_BRAND.background,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        p: { xs: 1.5, sm: 2.5 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 680,
          mx: "auto",
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${EMAIL_PREVIEW_BRAND.lightGray}`,
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3.5 }, py: { xs: 2.5, sm: 3 } }}>
          <Box
            component="img"
            src={EMAIL_PREVIEW_BRAND.logoUrl}
            alt={EMAIL_PREVIEW_BRAND.company}
            sx={{ height: 42, width: "auto", display: "block", mb: 3 }}
          />
          <Divider sx={{ mb: 3 }} />
          <Typography
            sx={{
              fontFamily: EMAIL_PREVIEW_BRAND.font,
              fontSize: { xs: 28, sm: 36 },
              lineHeight: 1.15,
              fontWeight: 800,
              color: EMAIL_PREVIEW_BRAND.text,
              mb: 3,
            }}
          >
            {normalizeText(subject) || "Untitled email"}
          </Typography>
          <EmailHeaderRows rows={headerRows} fontFamily={EMAIL_PREVIEW_BRAND.font} />
          <Divider sx={{ my: 3 }} />
          {forwardedMessage ? (
            <Typography sx={{ fontFamily: EMAIL_PREVIEW_BRAND.font, fontSize: 14, fontWeight: 800, mb: 1.5, color: EMAIL_PREVIEW_BRAND.text }}>
              Forwarder's message
            </Typography>
          ) : null}
          {renderBody({ htmlBody, textBody, fontFamily: EMAIL_PREVIEW_BRAND.font, textColor: EMAIL_PREVIEW_BRAND.text })}
          <ForwardedMessageSection
            forwardedMessage={forwardedMessage}
            fontFamily={EMAIL_PREVIEW_BRAND.font}
            textColor={EMAIL_PREVIEW_BRAND.text}
          />
          {attachments.length ? (
            <>
              <Divider sx={{ my: 3 }} />
              <AttachmentSection attachments={attachments} />
            </>
          ) : null}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontFamily: EMAIL_PREVIEW_BRAND.font, fontSize: 20, fontWeight: 600, mb: 1 }}>
              Questions or concerns?
            </Typography>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: 2.5,
                py: 1.25,
                borderRadius: 999,
                bgcolor: EMAIL_PREVIEW_BRAND.darkGray,
                color: "#fff",
                fontFamily: EMAIL_PREVIEW_BRAND.font,
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              Get in touch with the Elite Team
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const EmailViewer = ({
  layout = "plain",
  subject = "",
  fromName = "",
  fromEmail = "",
  to = [],
  cc = [],
  bcc = [],
  htmlBody = "",
  textBody = "",
  sentAt = "",
  receivedAt = "",
  attachments = [],
  forwardedMessage = null,
  extraHeaderRows = [],
}) => {
  const normalizedLayout = normalizeEmailViewerLayout(layout);
  const viewerProps = {
    subject,
    fromName,
    fromEmail,
    to,
    cc,
    bcc,
    htmlBody,
    textBody,
    sentAt,
    receivedAt,
    attachments: Array.isArray(attachments) ? attachments : [],
    forwardedMessage,
    extraHeaderRows: Array.isArray(extraHeaderRows) ? extraHeaderRows : [],
  };

  if (normalizedLayout === "elite") {
    return <EliteTemplateEmailViewer {...viewerProps} />;
  }

  return <PlainEmailViewer {...viewerProps} />;
};

export default EmailViewer;
